import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, X, Plus, ArrowLeft, ArrowRight, Loader2, Home, Building2, Check } from "lucide-react";
import { api, apiError, peso } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ConsumerHeader } from "@/pages/ConsumerHome";
import { toast } from "sonner";

import { PROPERTY_TYPE_GROUPS, propertyTypeLabel } from "@/lib/taxonomy";
const TIMINGS = ["Immediately", "Within 30 days", "1-3 months", "3-6 months", "6-12 months", "Just exploring"];
const FINANCING = ["Cash", "Bank Financing", "Developer Financing", "Not Sure"];

function LocationPicker({ locations, setLocations }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const t = useRef(null);
  const search = (v) => {
    setQ(v);
    clearTimeout(t.current);
    if (v.trim().length < 2) { setRes([]); return; }
    t.current = setTimeout(async () => {
      try { const { data } = await api.get(`/geo/search?q=${encodeURIComponent(v)}`); setRes((data.results || []).slice(0, 6)); } catch { setRes([]); }
    }, 250);
  };
  const add = (r) => {
    if (!locations.find((l) => l.code === r.code)) setLocations([...locations, { code: r.code, name: r.name }]);
    setQ(""); setRes([]);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {locations.map((l) => (
          <span key={l.code} data-testid={`loc-chip-${l.code}`} className="inline-flex items-center gap-1 bg-accent/10 text-accent text-xs font-medium px-2.5 py-1 rounded-full">
            {l.name}<button onClick={() => setLocations(locations.filter((x) => x.code !== l.code))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="req-location-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Search city / municipality / province" className="pl-9 h-11" />
        {res.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
            {res.map((r) => (
              <li key={r.code}><button data-testid={`loc-opt-${r.code}`} onClick={() => add(r)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">{r.name}<span className="text-xs text-slate-400 ml-1">{r.parentName || r.regionName || ""}</span></button></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const L = ({ children }) => <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{children}</label>;
const Flex = ({ id, checked, onChange }) => (
  <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1.5">
    <Checkbox data-testid={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} /> Required (must match)
  </label>
);

export default function ConsumerRequirement() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [step, setStep] = useState(editing ? 2 : 1);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editing);
  const [f, setF] = useState({
    listing_type: "For Sale", locations: [], property_type: "Any", property_type_flexible: true,
    budget_min: "", budget_max: "", financing_intent: "", min_bedrooms: "", max_bedrooms: "",
    min_bathrooms: "", parking_min: "", floor_area_min: "", lot_area_min: "", project: "",
    preferred_features: "", timing: "", location_flexible: true, bedrooms_flexible: true, budget_flexible: true,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const rent = f.listing_type === "For Rent";

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/me/requirements/${id}`);
        setF((p) => ({
          ...p, listing_type: data.listing_type || "For Sale",
          locations: (data.location_codes || []).map((c, i) => ({ code: c, name: (data.cities || [])[i] || c })),
          property_type: data.property_type || "Any", property_type_flexible: data.property_type_flexible !== false,
          budget_min: data.budget_min || "", budget_max: data.budget_max || "", financing_intent: data.financing_intent || "",
          min_bedrooms: data.min_bedrooms || "", max_bedrooms: data.max_bedrooms || "", min_bathrooms: data.min_bathrooms || "",
          parking_min: data.parking_min || "", floor_area_min: data.floor_area_min || "", lot_area_min: data.lot_area_min || "",
          project: data.project || "", preferred_features: (data.preferred_features || []).join(", "), timing: data.timing || "",
          location_flexible: data.location_flexible !== false, bedrooms_flexible: data.bedrooms_flexible !== false,
          budget_flexible: data.budget_flexible !== false,
        }));
        setLoaded(true);
      } catch (e) { toast.error(apiError(e)); nav("/"); }
    })();
  }, [id]);

  const payload = () => ({
    listing_type: f.listing_type,
    location_codes: f.locations.map((l) => l.code), cities: f.locations.map((l) => l.name),
    property_type: f.property_type, property_type_flexible: f.property_type_flexible,
    budget_min: Number(f.budget_min) || 0, budget_max: Number(f.budget_max) || 0, budget_flexible: f.budget_flexible,
    financing_intent: rent ? "" : f.financing_intent,
    min_bedrooms: Number(f.min_bedrooms) || 0, max_bedrooms: f.max_bedrooms ? Number(f.max_bedrooms) : null, bedrooms_flexible: f.bedrooms_flexible,
    min_bathrooms: Number(f.min_bathrooms) || 0, parking_min: Number(f.parking_min) || 0,
    floor_area_min: f.floor_area_min ? Number(f.floor_area_min) : null, lot_area_min: f.lot_area_min ? Number(f.lot_area_min) : null,
    project: f.project, location_flexible: f.location_flexible, timing: f.timing,
    preferred_features: f.preferred_features ? f.preferred_features.split(",").map((s) => s.trim()).filter(Boolean) : [],
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/me/requirements/${id}`, payload());
      else await api.post("/me/requirements", payload());
      toast.success(editing ? "Requirement updated" : "Requirement saved");
      nav("/");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const budgetLabel = rent ? "Monthly rent" : "Budget";

  return (
    <div className="min-h-screen bg-background">
      <ConsumerHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => (step > 1 && !editing ? setStep(step - 1) : nav("/"))} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

        {!loaded && (
          <div data-testid="req-loading" className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        )}

        {loaded && step === 1 && (
          <div data-testid="req-intent-step">
            <h1 className="font-display text-3xl font-bold text-slate-900">I'm looking for a property</h1>
            <p className="text-slate-500 mt-1 mb-6">What are you looking to do?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[["For Sale", "Buy", "Find a property to purchase."], ["For Rent", "Rent", "Find a property to rent."]].map(([lt, title, desc]) => (
                <button key={lt} data-testid={`req-intent-${title.toLowerCase()}`} onClick={() => { set("listing_type", lt); setStep(2); }}
                  className="text-left rounded-2xl border border-slate-200 p-5 hover:border-accent hover:shadow-md transition-all">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center mb-3">{title === "Buy" ? <Home className="h-5 w-5 text-accent" /> : <Building2 className="h-5 w-5 text-accent" />}</div>
                  <div className="font-semibold text-slate-900">{title}</div>
                  <div className="text-sm text-slate-500 mt-1">{desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {loaded && step === 2 && (
          <div data-testid="req-builder-step" className="space-y-5">
            <h1 className="font-display text-2xl font-bold text-slate-900">{rent ? "Rent" : "Buy"} requirement</h1>
            <div><L>Preferred location(s)</L><LocationPicker locations={f.locations} setLocations={(v) => set("locations", v)} /><Flex id="req-location-required" checked={!f.location_flexible} onChange={(v) => set("location_flexible", !v)} /></div>
            <div>
              <L>Property type</L>
              <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="req-property-type" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Any">Any / Flexible</SelectItem>{PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}</SelectContent>
              </Select>
              {f.property_type !== "Any" && <Flex id="req-type-required" checked={!f.property_type_flexible} onChange={(v) => set("property_type_flexible", !v)} />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><L>{budgetLabel} min (₱)</L><CurrencyInput data-testid="req-budget-min" value={f.budget_min} onValueChange={(v) => set("budget_min", v)} className="h-11" /></div>
              <div><L>{budgetLabel} max (₱)</L><CurrencyInput data-testid="req-budget-max" value={f.budget_max} onValueChange={(v) => set("budget_max", v)} className="h-11" /></div>
            </div>
            <Flex id="req-budget-required" checked={!f.budget_flexible} onChange={(v) => set("budget_flexible", !v)} />
            {!rent && (
              <div><L>Financing intent</L>
                <Select value={f.financing_intent} onValueChange={(v) => set("financing_intent", v)}>
                  <SelectTrigger data-testid="req-financing" className="h-11"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                  <SelectContent>{FINANCING.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><L>Bedrooms (min)</L><Input data-testid="req-bedrooms" type="number" value={f.min_bedrooms} onChange={(e) => set("min_bedrooms", e.target.value)} className="h-11" /></div>
              <div><L>Bathrooms (min)</L><Input data-testid="req-bathrooms" type="number" value={f.min_bathrooms} onChange={(e) => set("min_bathrooms", e.target.value)} className="h-11" /></div>
              <div><L>Parking (min)</L><Input data-testid="req-parking" type="number" value={f.parking_min} onChange={(e) => set("parking_min", e.target.value)} className="h-11" /></div>
            </div>
            <Flex id="req-bedrooms-required" checked={!f.bedrooms_flexible} onChange={(v) => set("bedrooms_flexible", !v)} />
            <div className="grid grid-cols-2 gap-3">
              <div><L>Floor area min (sqm)</L><Input data-testid="req-floor-area" type="number" value={f.floor_area_min} onChange={(e) => set("floor_area_min", e.target.value)} className="h-11" /></div>
              <div><L>Lot area min (sqm)</L><Input data-testid="req-lot-area" type="number" value={f.lot_area_min} onChange={(e) => set("lot_area_min", e.target.value)} className="h-11" /></div>
            </div>
            <div><L>Preferred project / development</L><Input data-testid="req-project" value={f.project} onChange={(e) => set("project", e.target.value)} placeholder="Optional" className="h-11" /></div>
            <div><L>Preferred Features &amp; Amenities</L><Input data-testid="req-features" value={f.preferred_features} onChange={(e) => set("preferred_features", e.target.value)} placeholder="Comma separated (optional)" className="h-11" /></div>
            <div><L>Timing</L>
              <Select value={f.timing} onValueChange={(v) => set("timing", v)}>
                <SelectTrigger data-testid="req-timing" className="h-11"><SelectValue placeholder="When are you looking?" /></SelectTrigger>
                <SelectContent>{TIMINGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="req-to-review" onClick={() => setStep(3)} className="w-full h-11 bg-accent text-accent-foreground rounded-xl font-semibold">Review <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        )}

        {loaded && step === 3 && (
          <div data-testid="req-review-step" className="space-y-4">
            <h1 className="font-display text-2xl font-bold text-slate-900">Review your requirement</h1>
            <div className="bg-card border border-border rounded-xl divide-y divide-slate-100">
              {[
                ["Looking to", rent ? "Rent" : "Buy"],
                ["Location(s)", f.locations.map((l) => l.name).join(", ") || "Any", !f.location_flexible],
                ["Property type", f.property_type === "Any" ? "Any / Flexible" : f.property_type, f.property_type !== "Any" && !f.property_type_flexible],
                [rent ? "Monthly rent" : "Budget", (f.budget_min || f.budget_max) ? `${peso(Number(f.budget_min) || 0)} – ${peso(Number(f.budget_max) || 0)}` : "Any", !f.budget_flexible],
                ...(!rent && f.financing_intent ? [["Financing", f.financing_intent]] : []),
                ["Bedrooms", f.min_bedrooms ? `${f.min_bedrooms}+` : "Any", !f.bedrooms_flexible],
                ["Bathrooms", f.min_bathrooms ? `${f.min_bathrooms}+` : "Any"],
                ["Parking", f.parking_min ? `${f.parking_min}+` : "Any"],
                ["Floor area", f.floor_area_min ? `${f.floor_area_min}+ sqm` : "Any"],
                ["Lot area", f.lot_area_min ? `${f.lot_area_min}+ sqm` : "Any"],
                ["Project", f.project || "Any"],
                ["Features", f.preferred_features || "None"],
                ["Timing", f.timing || "Not specified"],
              ].map(([k, v, req], i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-medium text-slate-800 flex items-center gap-2">{v}{req && <span className="text-[10px] font-bold uppercase bg-accent/10 text-accent px-1.5 py-0.5 rounded">Required</span>}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-11 rounded-xl">Edit</Button>
              <Button data-testid="req-save" onClick={save} disabled={saving} className="flex-1 h-11 bg-accent text-accent-foreground rounded-xl font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save Requirement <Check className="h-4 w-4 ml-1" /></>}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
